import React from 'react'
import Form from 'components/Form'
import PasswordInput from 'components/PasswordInput'
import Button from 'components/Button'
import Modal from 'components/Modal'

import { ModalInjectedProps } from 'containers/ModalManager'
import { useToast } from 'containers/ToastManager'

import * as yup from 'yup'
import { Formik, FormikHelpers } from 'formik'
import { useUserPasswordUpdateMutation } from 'gqlTypes'
import useSignOut from 'modules/user/useSignOut'
import { ErrorHandler } from 'modules/tracing'
import { NewPasswordCaption, StyledForm } from './ChangePasswordModal.styles'

const SCHEMA = yup.object().shape({
  password: yup.string().required('Current Password can’t be blank'),
  newPassword: yup
    .string()
    .min(8, 'Password is too short (minimum is 8 characters)')
    .max(72, 'Password is too long (maximum is 72 characters')
    .required('New Password can’t be blank'),
  confirmNewPassword: yup
    .string()
    .oneOf([yup.ref('newPassword'), null], 'Passwords don’t match')
    .required('Confirm New Password can’t be blank'),
})

interface FormValues {
  password: string
  newPassword: string
  confirmNewPassword: string
}

type FormActions = FormikHelpers<FormValues>

interface ChangePasswordModalProps extends ModalInjectedProps {}

export const ChangePasswordModal = ({
  hideModal,
}: ChangePasswordModalProps) => {
  const signOut = useSignOut()
  const { showToast } = useToast()

  const [updatePassword] = useUserPasswordUpdateMutation({
    redirectErrors: true,
    onCompleted: data => {
      if (!data || !data.userPasswordUpdate) {
        return ErrorHandler.shouldNeverHappen('Could not update password')
      }

      /*
      Force the user to re-login
      https://github.com/sketch-hq/Cloud/issues/2731#issuecomment-563952005
      */
      signOut()
      showToast('Your password has been updated. Please sign in')
      hideModal()
    },
    onError: error => {
      showToast(error.message, 'negative')
    },
  })

  const handleSubmit = async (values: FormValues, actions: FormActions) => {
    await updatePassword({
      variables: {
        currentPassword: values.password,
        newPassword: values.newPassword,
      },
    })

    actions.setSubmitting(false)
  }

  return (
    <Modal title="Change Your Password" onCancel={hideModal}>
      <Formik
        initialValues={{
          password: '',
          newPassword: '',
          confirmNewPassword: '',
        }}
        validationSchema={SCHEMA}
        onReset={hideModal}
        onSubmit={handleSubmit}>
        {({
          isSubmitting,
          handleReset,
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isValid,
          dirty,
        }) => (
          <>
            <Modal.Body>
              <StyledForm id="change-password-form">
                <Form.Field
                  name="password"
                  label="Password"
                  errorText={touched.password ? errors.password : undefined}>
                  <PasswordInput
                    name="password"
                    aria-label="Password"
                    value={values.password}
                    disabled={isSubmitting}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Form.Field>
                <Form.Field
                  name="newPassword"
                  label="New Password"
                  errorText={
                    touched.newPassword ? errors.newPassword : undefined
                  }>
                  <>
                    <PasswordInput
                      name="newPassword"
                      aria-label="New Password"
                      value={values.newPassword}
                      disabled={isSubmitting}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <NewPasswordCaption>
                      Should be 8 or more characters
                    </NewPasswordCaption>
                  </>
                </Form.Field>
                <Form.Field
                  name="confirmNewPassword"
                  label="Confirm New Password"
                  errorText={
                    touched.confirmNewPassword
                      ? errors.confirmNewPassword
                      : undefined
                  }>
                  <PasswordInput
                    name="confirmNewPassword"
                    aria-label="Confirm New Password"
                    value={values.confirmNewPassword}
                    disabled={isSubmitting}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Form.Field>
              </StyledForm>
            </Modal.Body>
            <Modal.Footer>
              <Button
                type="reset"
                variant="secondary"
                disabled={isSubmitting}
                onClick={handleReset}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={isSubmitting}
                disabled={isSubmitting || !isValid || !dirty}
                form="change-password-form">
                Change Password
              </Button>
            </Modal.Footer>
          </>
        )}
      </Formik>
    </Modal>
  )
}
